import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ExternalLink, Search, FileText, Phone, ArrowRight, MapPin, ShieldCheck } from 'lucide-react';

// ── Long Island Water Quality Guide ──────────────────────────────────────────
// This page replaces the previous town-by-town water information section.
// It directs users to EWG.org for local water quality data, then invites
// them to contact Aquify for a professional water evaluation.

const WaterGuidePage = () => {
  const steps = [
    {
      number: '1',
      icon: ExternalLink,
      title: 'Go to EWG.org',
      description:
        'Visit the Environmental Working Group\'s Tap Water Database at ewg.org/tapwater to access water quality data for your area.',
      action: {
        label: 'Visit EWG.org Tap Water Database',
        href: 'https://www.ewg.org/tapwater/',
      },
    },
    {
      number: '2',
      icon: Search,
      title: 'Search Your ZIP Code or Water Utility',
      description:
        'Enter your ZIP code or the name of your local water utility in the search bar. EWG\'s database covers thousands of utilities across the country, including those serving Long Island, New York City, and South Florida.',
    },
    {
      number: '3',
      icon: FileText,
      title: 'Review Your Water Quality Report',
      description:
        'Your local utility\'s water report will show which contaminants have been detected, their measured levels, and how those levels compare to EWG\'s health guidelines and the EPA\'s legal limits. Pay close attention to contaminants flagged in red or orange — those exceed EWG\'s recommended health guidelines even if they technically meet federal legal limits.',
    },
    {
      number: '4',
      icon: Phone,
      title: 'Contact Aquify for a Professional Water Evaluation',
      description:
        'EWG reports reflect municipal supply data — they don\'t account for what happens to your water once it enters your home\'s pipes. Older plumbing, point-of-use conditions, well water, and other factors can significantly affect water quality at your tap. Contact Aquify to schedule a complimentary in-home water evaluation for a complete picture.',
      action: {
        label: 'Request a Complimentary Water Evaluation',
        href: '/contact',
      },
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <section className="py-16 bg-slate-50 border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#1571BA' }}>
            Water Quality Resources
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-5 leading-tight">
            Know What's in Your Water
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
            The Environmental Working Group (EWG) maintains a free, searchable database of local water
            quality reports for utilities across the United States — including Long Island, New York City,
            and South Florida. Here's how to use it.
          </p>
          <a
            href={`${process.env.PUBLIC_URL}/aquify-water-guide.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="aq-primary-btn inline-flex items-center gap-2 font-bold px-8 py-4 rounded-xl transform hover:scale-105 shadow-md text-lg"
          >
            <FileText className="h-5 w-5" />
            View the Aquify Water Guide
          </a>
        </div>
      </section>

      {/* ── Step-by-Step Guide ───────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How to Look Up Your Local Water Quality
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Follow these four steps to find out what contaminants have been detected in your
              local water supply — and what to do next.
            </p>
          </div>

          <div className="space-y-10">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div
                  key={idx}
                  className="flex gap-6 p-8 rounded-2xl border border-gray-200 bg-white hover:shadow-lg transition-shadow duration-300"
                >
                  {/* Step number */}
                  <div className="flex-shrink-0">
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-md"
                      style={{ background: 'linear-gradient(135deg, #0C4288 0%, #1571BA 100%)' }}
                    >
                      {step.number}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: '#E3F2FD' }}
                      >
                        <Icon className="h-4 w-4" style={{ color: '#1571BA' }} />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900">{step.title}</h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed mb-4">{step.description}</p>
                    {step.action && (
                      <a
                        href={step.action.href}
                        target={step.action.href.startsWith('/') ? undefined : "_blank"}
                        rel={step.action.href.startsWith('/') ? undefined : "noopener noreferrer"}
                        className="aq-primary-btn inline-flex items-center gap-2 font-semibold px-6 py-3 rounded-lg transform hover:scale-105 shadow-md text-sm"
                      >
                        {step.action.label}
                        <ArrowRight className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Important Context ───────────────────────────────────────────── */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div
            className="rounded-2xl p-8 border-l-4"
            style={{ backgroundColor: '#FFF9E6', borderColor: '#F59E0B' }}
          >
            <h3 className="text-xl font-bold text-slate-900 mb-3">
              Important: EWG Data Has Limits
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              EWG's Tap Water Database is a valuable starting point, but it only reflects testing
              data submitted by your municipal utility. It does not account for:
            </p>
            <ul className="space-y-2 text-gray-700">
              {[
                'Contaminants introduced by older or corroded pipes inside your home',
                'Lead or copper leaching from household plumbing fixtures',
                'Well water conditions (not reported through municipal systems)',
                'Seasonal fluctuations in water quality',
                'Localized contamination from nearby sources',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#F59E0B' }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              For a complete and accurate picture of your water quality at the tap, a professional
              in-home evaluation is the most reliable option.
            </p>
          </div>
        </div>
      </section>

      {/* ── Service Area Note ─────────────────────────────────────────────── */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <MapPin className="h-6 w-6" style={{ color: '#1571BA' }} />
                Aquify Serves These Areas
              </h2>
              <div className="flex flex-wrap gap-2 mb-5">
                {[
                  'Nassau County', 'Suffolk County', 'New York City',
                  'Westchester', 'Palm Beach County', 'Broward County',
                ].map((area) => (
                  <span
                    key={area}
                    className="px-4 py-1.5 rounded-full text-sm font-semibold"
                    style={{ backgroundColor: '#E3F2FD', color: '#0C4288', border: '1px solid #BFDBFE' }}
                  >
                    {area}
                  </span>
                ))}
              </div>
              <p className="text-gray-600 leading-relaxed">
                If your area is listed above, Aquify can schedule a complimentary in-home water
                evaluation at no cost or obligation to you. Don't see your area? Call us anyway —
                we may still be able to help.
              </p>
            </div>
            <div
              className="rounded-2xl p-8"
              style={{ background: 'linear-gradient(135deg, #0C4288 0%, #1571BA 100%)' }}
            >
              <ShieldCheck className="h-10 w-10 text-white mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">
                What Aquify Does That EWG Can't
              </h3>
              <ul className="space-y-2 text-blue-100 text-sm">
                {[
                  'Tests your actual tap water in person',
                  'Identifies issues specific to your home\u2019s plumbing',
                  'Provides a custom solution designed for your water chemistry',
                  'Installs systems that are professionally designed and licensed-plumber installed',
                  'Gives you a clear answer — not just a report',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-[#50BAED]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────────────────── */}
      <section
        className="py-20"
        style={{ background: 'linear-gradient(135deg, #0C4288 0%, #1571BA 100%)' }}
      >
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">
            Ready for a Professional Water Evaluation?
          </h2>
          <p className="text-[#E3F2FD] text-lg mb-8 leading-relaxed">
            Don't stop at the EWG report. Schedule your complimentary in-home evaluation with
            Aquify — no cost, no obligation, no pressure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="aq-primary-btn inline-flex items-center justify-center gap-2 font-bold text-lg px-10 py-4 rounded-xl transform hover:scale-105 shadow-lg"
              style={{ border: '2px solid white' }}
            >
              Request a Free Evaluation
              <ArrowRight className="h-5 w-5" />
            </a>
            <a
              href="tel:5167299013"
              className="inline-flex items-center justify-center gap-2 font-bold text-lg px-10 py-4 rounded-xl transition-all duration-300 hover:scale-105"
              style={{ border: '2px solid rgba(255,255,255,0.6)', color: '#ffffff' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <Phone className="h-5 w-5" />
              516-729-9013
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WaterGuidePage;
